import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import styled from "styled-components";

import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register Chart.js modules
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

const ChartWrapper = styled.div`
  position: relative; /* anchor absolute icon */
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const GREEN = "rgba(46, 125, 50, 0.7)";     // bars – current period
const GREEN_BORDER = "rgba(46, 125, 50, 1)";
const LINE_NEUTRAL = "rgba(120, 120, 120, 1)"; // neutral gray for cumulative %

const ParetoChart = ({ data }) => {
  const [salesData, setSalesData] = useState([]);
  const [error, setError] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("full");

  const calculateSalesValue = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  // Build grouped structure: salesData[year][month][contractor] = sum
  useEffect(() => {
    if (!data || data.length === 0) return;

    try {
      const grouped = data.reduce((acc, row) => {
        const year = parseInt(row.Rok, 10);
        const month = parseInt(row.Miesiac, 10);
        const contractor = row.NazwaKontrahenta || "—";
        const sales = calculateSalesValue(row);

        if (!acc[year]) acc[year] = {};
        if (!acc[year][month]) acc[year][month] = {};
        if (!acc[year][month][contractor]) acc[year][month][contractor] = 0;
        acc[year][month][contractor] += sales;

        return acc;
      }, {});

      const years = Object.keys(grouped).map(Number).sort((a, b) => b - a);
      const latestYear = years[0];

      const months = Object.keys(grouped[latestYear] || {})
        .map(Number)
        .sort((a, b) => a - b);

      setAvailableYears(years);
      setAvailableMonths(months);
      setSelectedYear(latestYear);
      setSelectedMonth(months.length ? String(Math.max(...months)) : "full");
      setSalesData(grouped);
    } catch (e) {
      setError("Błąd podczas przetwarzania danych.");
    }
  }, [data]);

  // Prepare chart data (uniform green bars)
  useEffect(() => {
    if (!selectedYear || !salesData[selectedYear]) return;

    let contractors = {};

    if (selectedMonth === "full") {
      // Sum across all months in selected year
      Object.values(salesData[selectedYear]).forEach((monthData) => {
        Object.entries(monthData).forEach(([name, sales]) => {
          if (!contractors[name]) contractors[name] = 0;
          contractors[name] += sales;
        });
      });
    } else {
      contractors = salesData[selectedYear][parseInt(selectedMonth, 10)] || {};
    }

    const sorted = Object.entries(contractors)
      .sort(([, a], [, b]) => b - a)
      .map(([name, sales]) => ({ name, sales }));

    const total = sorted.reduce((s, c) => s + c.sales, 0) || 1;

    let cum = 0;
    const pareto = sorted.map((c) => {
      cum += c.sales;
      return { ...c, cumulativePercentage: (cum / total) * 100 };
    });

    // Keep only up to 80% cumulative (Pareto)
    const filtered = pareto.filter((c) => c.cumulativePercentage <= 80);

    setChartData({
      labels: filtered.map((c) => c.name),
      datasets: [
        {
          type: "bar",
          label: "Wartość Sprzedaży (PLN)",
          data: filtered.map((c) => c.sales),
          backgroundColor: GREEN,         // <- uniform green (no red max)
          borderColor: GREEN_BORDER,
          borderWidth: 1,
        },
        {
          type: "line",
          label: "Kumulacyjny Procent (%)",
          data: filtered.map((c) => c.cumulativePercentage),
          borderColor: LINE_NEUTRAL,
          pointBackgroundColor: LINE_NEUTRAL,
          borderWidth: 2,
          yAxisID: "y1",
          tension: 0.3,
          fill: false,
        },
      ],
    });
  }, [selectedYear, selectedMonth, salesData]);

  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!salesData || availableYears.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="pareto-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="pareto-tooltip"
        place="left"
        content={
          <>
            <strong>Wykres Pareto</strong>
            <br />
            Pokazuje klientów generujących<br />
            80% sprzedaży w danym okresie.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:&nbsp;</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="month-select">Miesiąc:&nbsp;</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="full">Pełny rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {new Date(0, month - 1).toLocaleString("pl-PL", { month: "long" })}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Chart
          type="bar"
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                title: { display: true, text: "Sprzedaż" },
                beginAtZero: true,
                ticks: {
                  callback: (value) =>
                    value.toLocaleString("pl-PL", {
                      useGrouping: true,
                      maximumFractionDigits: 0,
                    }),
                },
              },
              y1: {
                position: "right",
                title: { display: true, text: "Kumulacyjny Procent (%)" },
                beginAtZero: true,
                max: 100,
                ticks: { callback: (value) => `${value}%` },
              },
              x: {
                title: { display: true, text: "Kontrahenci" },
                ticks: {
                  callback: function (value) {
                    const label = this.getLabelForValue(value);
                    return label.length > 15 ? label.slice(0, 15) + "..." : label;
                  },
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: `Wykres Pareto: klienci generujący 80% sprzedaży (${selectedYear}${
                  selectedMonth === "full"
                    ? ", pełny rok"
                    : `, ${new Date(0, parseInt(selectedMonth, 10) - 1).toLocaleString("pl-PL", {
                        month: "long",
                      })}`
                })`,
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.raw;
                    if (context.dataset.type === "bar") {
                      return `Sprzedaż: ${value.toLocaleString("pl-PL", {
                        useGrouping: true,
                        maximumFractionDigits: 0,
                      })}`;
                    } else {
                      return `Kumulacyjny Procent: ${Number(value).toFixed(2)}%`;
                    }
                  },
                },
              },
              legend: { display: true },
            },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default ParetoChart;
